import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { initialStatus, LoadingStatus } from '@bcef/ia-shared';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { debounceTime, pipe, switchMap, tap } from 'rxjs';

import { ErrorStore } from '@shared/store/error.store';

import { IfiSimulationRequest } from '../interfaces/ifi-simulation-request';
import { IfiSimulationResponse } from '../interfaces/ifi-simulation-response.interface';
import { IfiSimulationService } from '../services/ifi-simulation.service';

export interface IfiState {
  ifiSimulationRequest: Partial<IfiSimulationRequest>;
  ifiSimulationResponse: Partial<IfiSimulationResponse>;
  loadingStatus: LoadingStatus;
  assets: number;
  passifs: number;
  reductions: number;
}
export const initialState: IfiState = {
  ifiSimulationRequest: {},
  ifiSimulationResponse: {},
  loadingStatus: initialStatus,
  assets: 0,
  passifs: 0,
  reductions: 0,
};

export const SEND_DONATION_FORM_DEBOUNCE_MILLIS = 300;

export const IfiStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withStorageSync({ key: 'ia-simulateur-epargne:ifi', storage: () => sessionStorage }),
  withMethods(
    (
      store,
      ifiSimulationService = inject(IfiSimulationService),
      errorStore = inject(ErrorStore),
      router = inject(Router),
    ) => ({
      sendIfiForm: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              loadingStatus: {
                ...state.loadingStatus,
                isLoading: true,
              },
            })),
          ),
          debounceTime(SEND_DONATION_FORM_DEBOUNCE_MILLIS),
          switchMap(() =>
            ifiSimulationService.postIfiSimulation(store.ifiSimulationRequest()).pipe(
              tapResponse({
                next: (ifiSimulationResponse) => {
                  patchState(store, (state) => ({
                    ifiSimulationResponse: ifiSimulationResponse,
                    loadingStatus: { ...state.loadingStatus, isLoaded: true },
                  }));
                  router.navigate(['ifi', 'summary']);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setError(error);
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, error },
                  }));
                  router.navigate(['erreur']);
                },
                finalize: () =>
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, isLoading: false },
                  })),
              }),
            ),
          ),
        ),
      ),
      addResponses(ifiSimulationRequest: Partial<IfiSimulationRequest>): void {
        patchState(store, { ifiSimulationRequest });
      },
      setCalculatedValues(assets: number, passifs: number, reductions: number): void {
        patchState(store, {
          assets,
          passifs,
          reductions,
        });
      },
    }),
  ),
);
export type IIfiStore = InstanceType<typeof IfiStore>;
